import React from 'react';
import { RotateCcw, Play, Clock, Trophy, PlusSquare, QrCode, AlertTriangle } from 'lucide-react';
import { PersistedMatchData } from '../../hooks/useMatchPersistence';
import { SetupData } from '../../hooks/useMatchData';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface MatchRecoveryModalProps {
  /** Match sauvegardé sur l'appareil (bloc de gauche) */
  savedMatch: PersistedMatchData;
  /** Payload reçu en paramètre (?data=), s'il y en a un (bloc de droite) */
  incomingMatch?: any | null;
  /** Configuration issue d'un scan de QR code de court, s'il y en a une */
  setupData?: SetupData | null;
  onRestore: () => void;
  onStartNew: () => void;
}

/**
 * Ligne libellé / valeur d'une fiche match.
 * Disposition en ligne : l'arbitre consulte cette modale sur un téléphone en
 * paysage (~360 px de haut), les deux boutons doivent rester visibles sans
 * défilement.
 */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 items-baseline">
      <span className="w-[74px] shrink-0 pr-1 text-[10px] leading-4 font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <span className="flex-1 text-sm text-gray-900 break-words">{value}</span>
    </div>
  );
}

/**
 * Tableau de scores compact du match sauvegardé : sets gagnés, jeux terminés,
 * jeu en cours, puis cases vides pour les jeux restants.
 */
function SavedScoreboard({ matchState }: { matchState: any }) {
  const maxGames = matchState.mi.st === 'BestOf5' ? 5 : 3;
  const games: any[] = matchState.gms || [];
  const isMatchFinished = matchState.ended === true;
  const hasCurrentGame = !isMatchFinished && (matchState.gs[0] > 0 || matchState.gs[1] > 0);
  const remainingGames = Math.max(0, maxGames - games.length - (hasCurrentGame ? 1 : 0));

  const row = (player: 'A' | 'B') => {
    const idx = player === 'A' ? 0 : 1;
    const gameScoreIdx = player === 'A' ? 1 : 2;
    return (
      <div className={`flex items-center ${player === 'A' ? 'border-b border-gray-200' : ''}`}>
        <div
          className="w-1 self-stretch"
          style={{ backgroundColor: player === 'A' ? matchState.cA : matchState.cB }}
        />
        <div className="flex-1 px-2 py-1.5 text-sm font-medium text-gray-900 truncate">
          {player === 'A' ? matchState.mi.pA : matchState.mi.pB}
        </div>
        <div className="px-2 py-1.5 text-base font-bold text-gray-700">
          {matchState.sets[idx]}
        </div>
        {games.map((game: any[], i: number) => (
          <div
            key={i}
            className="w-7 h-7 flex items-center justify-center text-xs font-medium text-white mx-0.5"
            style={{ backgroundColor: game[0] === player ? '#22c55e' : '#6b7280' }}
          >
            {game[gameScoreIdx]}
          </div>
        ))}
        {hasCurrentGame && (
          <div className="w-7 h-7 flex items-center justify-center text-xs font-medium text-gray-700 bg-gray-100 mx-0.5">
            {matchState.gs[idx]}
          </div>
        )}
        {Array.from({ length: remainingGames }).map((_, i) => (
          <div
            key={`empty-${player}-${i}`}
            className="w-7 h-7 flex items-center justify-center text-xs font-medium text-gray-400 bg-gray-50 mx-0.5"
          >
            -
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200">
      {row('A')}
      {row('B')}
    </div>
  );
}

export function MatchRecoveryModal({
  savedMatch,
  incomingMatch = null,
  setupData = null,
  onRestore,
  onStartNew
}: MatchRecoveryModalProps) {
  const { t } = useTranslation();

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - timestamp;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 60) {
      return t('screens.recovery.timeAgo.minutes', { params: { count: diffMins } });
    } else if (diffMins < 1440) { // moins de 24h
      const diffHours = Math.floor(diffMins / 60);
      return t('screens.recovery.timeAgo.hours', { params: { count: diffHours } });
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
  };

  const getMatchProgress = () => {
    const { progress, matchState } = savedMatch;

    if (progress.isFinished) {
      return {
        status: t('screens.recovery.status.finished'),
        color: 'text-green-600',
        icon: Trophy,
        description: t('screens.recovery.matchFinished', {
          params: {
            scoreA: (matchState as any).sets[0],
            scoreB: (matchState as any).sets[1]
          }
        })
      };
    } else if (progress.isStarted) {
      return {
        status: t('screens.recovery.status.inProgress'),
        color: 'text-blue-600',
        icon: Play,
        description: `${t('screens.recovery.gamesPlayed', { params: { count: progress.gamesPlayed } })} · ${t('screens.recovery.pointsPlayed', { params: { count: progress.pointsPlayed } })}`
      };
    } else {
      return {
        status: t('screens.recovery.status.configured'),
        color: 'text-orange-600',
        icon: Clock,
        description: t('screens.recovery.matchConfigured')
      };
    }
  };

  const progressInfo = getMatchProgress();
  const IconComponent = progressInfo.icon;
  const saved = savedMatch.matchState as any;

  // Trois variantes pour le bloc de droite, selon ce que l'app a reçu au lancement
  const newMatchMode: 'incoming' | 'qr' | 'manual' =
    incomingMatch ? 'incoming' : setupData ? 'qr' : 'manual';

  const newMatchLabel =
    newMatchMode === 'incoming'
      ? t('screens.recovery.startThisMatch')
      : t('screens.recovery.createNewMatch');

  const NewMatchIcon = newMatchMode === 'qr' ? QrCode : PlusSquare;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white p-3 sm:p-4 max-w-3xl w-full max-h-[96vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-orange-100 p-1.5 shrink-0">
            <RotateCcw className="w-4 h-4 text-orange-600" />
          </div>
          <h2 className="text-base font-semibold truncate">{t('screens.recovery.title')}</h2>
          <span className="ml-auto text-xs text-gray-600 hidden sm:inline">
            {t('screens.recovery.chooseAction')}
          </span>
        </div>

        {/* Deux colonnes : match en cours (gauche) / nouveau match (droite) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

          {/* ── Gauche : le match déjà en cours ─────────────────── */}
          <section className="flex flex-col border-2 border-green-600 bg-gray-50 p-2.5">
            <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-gray-300">
              <IconComponent className={`w-4 h-4 shrink-0 ${progressInfo.color}`} />
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">
                {t('screens.recovery.currentMatch')}
              </h3>
              <span className={`ml-auto text-xs font-medium ${progressInfo.color}`}>
                {progressInfo.status}
              </span>
            </div>

            <div className="space-y-1 mb-2">
              <Field
                label={t('screens.recovery.competition')}
                value={saved.mi.comp || t('screens.recovery.unknownCompetition')}
              />
              <Field
                label={t('screens.recovery.match')}
                value={saved.mi.draw || t('screens.recovery.unknownDraw')}
              />
              <Field
                label={t('screens.recovery.players')}
                value={`${saved.mi.pA} vs ${saved.mi.pB}`}
              />
            </div>

            {savedMatch.progress.isStarted && (
              <div className="mb-2">
                <SavedScoreboard matchState={saved} />
              </div>
            )}

            <div className="text-[11px] leading-tight text-gray-500 mb-2">
              {progressInfo.description}
              <br />
              {t('screens.recovery.savedAt')} {formatTimestamp(savedMatch.timestamp)}
            </div>

            <button
              onClick={onRestore}
              className="mt-auto w-full bg-green-600 text-white py-2 px-3 font-semibold hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <RotateCcw size={16} />
              {t('screens.recovery.resumeMatch')}
            </button>
          </section>

          {/* ── Droite : le nouveau match ───────────────────────── */}
          <section className="flex flex-col border-2 border-blue-600 bg-blue-50 p-2.5">
            <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-blue-200">
              <NewMatchIcon className="w-4 h-4 shrink-0 text-blue-600" />
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">
                {t('screens.recovery.newMatch')}
              </h3>
            </div>

            {newMatchMode === 'incoming' && (
              <div className="space-y-1 mb-2">
                <Field
                  label={t('screens.recovery.competition')}
                  value={incomingMatch.competitionName}
                />
                <Field
                  label={t('screens.recovery.match')}
                  value={[incomingMatch.categoryName, incomingMatch.drawName]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Field
                  label={t('screens.recovery.players')}
                  value={`${incomingMatch.playerA} vs ${incomingMatch.playerB}`}
                />
                <Field
                  label={t('screens.recovery.court')}
                  value={[incomingMatch.centerName, incomingMatch.courtName]
                    .filter(Boolean)
                    .join(' - ') || '-'}
                />
                <Field
                  label={t('screens.recovery.format')}
                  value={[incomingMatch.scoringType, incomingMatch.dateTime]
                    .filter(Boolean)
                    .join(' · ')}
                />
              </div>
            )}

            {newMatchMode === 'qr' && (
              <div className="space-y-1 mb-2">
                <Field
                  label={t('screens.recovery.court')}
                  value={[setupData!.centerName, setupData!.courtName]
                    .filter(Boolean)
                    .join(' - ') || '-'}
                />
                <p className="text-xs text-gray-600">
                  {t('screens.recovery.qrHint')}
                </p>
              </div>
            )}

            {newMatchMode === 'manual' && (
              <p className="text-sm text-gray-600 mb-3">
                {t('screens.recovery.noIncomingMatch')}
              </p>
            )}

            {/* L'action ci-dessous est destructive : l'avertir juste avant le clic */}
            <div className="mt-auto mb-2 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-yellow-700 shrink-0 mt-0.5" />
              <p className="text-[11px] leading-tight text-yellow-800">
                {t('screens.recovery.discardWarning')}
              </p>
            </div>

            <button
              onClick={onStartNew}
              className="w-full bg-blue-600 text-white py-2 px-3 font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <NewMatchIcon size={16} />
              {newMatchLabel}
            </button>
          </section>
        </div>

      </div>
    </div>
  );
}
